import type { z, ZodObject } from 'zod'
import { toValue, ref, type MaybeRefOrGetter } from 'vue'
import { watchDebounced } from '@vueuse/core'

/**
 *
 * @param {T extends ZodObject} schema The Zod schema to validate against
 * @param {MaybeRefOrGetter<Record<string,unknown>>} data the data to validate
 * @param {TValidationOptions} opts options for validation composable
 * @param {TValidationOptions.mode} opts.mode 'eager' or 'lazy' validation. Eager will validate on execution, lazy will validate manually
 * @returns {Object} Object containing validation methods and properties
 * @returns {Function} validate - function to validate the data
 * @returns {Function} validateField - function to validate a specific field. Needs a key from the schema
 * @returns {Ref<boolean>} isValid - boolean ref indicating if the data is valid
 * @returns {Ref<Record<string, z.ZodIssue[]> | null>} errors - object containing the errors
 * @returns {Function} clearErrors - function to clear the errors
 * @returns {Function} scrollToError - function to scroll to the first error
 * @returns {Function} getError - function to get the error message for a specific path. Needs a key path
 * @example const { validate, validateField, isValid, errors, clearErrors, scrollToError, getError } = useZodFormValidation(schema, data, { mode: 'eager' })
 */

export type TValidationOptions = { mode: 'eager' | 'lazy' }

export default function useZodFormValidation<
  T extends ZodObject,
  D extends object = Record<string, unknown>,
>(schema: T, data: MaybeRefOrGetter<D>, opts?: TValidationOptions) {
  const { mode } = { ...{ mode: 'lazy' }, ...opts }

  const isValid = ref(true)
  const errors = ref<Record<string, z.ZodIssue[]> | null>(null)
  const initialData = ref(esCloneDeep(toValue(data)))

  const clearErrors = () => {
    errors.value = null
  }

  const resetForm = () => {
    clearErrors()
    isValid.value = true
  }

  const unwatch = ref<(() => void) | null>(null)

  const validationWatch = () => {
    if (unwatch.value) {
      return
    }
    unwatch.value = watchDebounced(
      () => JSON.stringify(toValue(data)),
      async () => {
        await validate()
      },
      { debounce: 250, maxWait: 500 }
    )
  }

  const validateField = async (key: keyof T['shape'] & string) => {
    if (unwatch.value) {
      return
    }

    clearErrors()

    const fieldSchema = schema.shape[key] as ZodObject

    if (!fieldSchema) {
      console.warn(
        `[useZodFormValidation] Field does not exist in the schema. Skipping standalone validation.`
      )
      return errors
    }

    const formData = toValue(data) as Record<string, unknown>
    const fieldValue = formData[key]

    const { success, error } = await fieldSchema.safeParseAsync(fieldValue)

    if (!success) {
      const mappedIssues = error.issues.map((issue) => ({
        ...issue,
        path: [key, ...issue.path],
      }))

      errors.value = esGroupBy(mappedIssues, 'path')
    }

    return errors
  }

  const validate = async () => {
    clearErrors()

    const { success, error } = await schema.safeParseAsync(toValue(data))

    isValid.value = success

    if (!success) {
      errors.value = esGroupBy(error.issues, 'path')
      validationWatch()
    }

    return errors
  }

  const scrollToError = (
    selector: string = '.z-form-error',
    options: {
      behavior?: 'smooth' | 'instant' | 'auto'
      block?: 'start' | 'center' | 'end' | 'nearest'
      inline?: 'start' | 'center' | 'end' | 'nearest'
    } = {
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest',
    }
  ) => {
    const el = document?.querySelector(selector)

    if (el) {
      el.scrollIntoView(options)
    }
  }

  const getError = (path: string) =>
    esGet(errors.value, `${path.replaceAll('.', ',')}.0.message`) as
      string | undefined

  if (mode === 'eager') {
    validationWatch()
  }

  const isFieldDirty = (path: string) => {
    const currentValue = esGet(toValue(data), path)
    const initialValue = esGet(initialData.value, path)

    return serializeValue(currentValue) !== serializeValue(initialValue)
  }

  const isFormDirty = (prefix?: string) => {
    const values = prefix ? esGet(toValue(data), prefix) : toValue(data)

    return Object.keys(values).some((key): boolean => {
      const path = `${prefix ? prefix + '.' : ''}${key}`

      if (esIsPlainObject(values[key])) {
        return isFormDirty(path)
      }

      return isFieldDirty(path)
    })
  }

  const isDirty = computed(() => isFormDirty())

  return {
    validate,
    validateField,
    isValid,
    errors,
    clearErrors,
    scrollToError,
    getError,
    isFieldDirty,
    resetForm,
    unwatch,
    isDirty,
  }
}
